import React, { createContext, useContext, ReactElement, ReactNode, CSSProperties } from "react";
import { Grid } from "@compass/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import { CardId } from "../types";

interface DashboardGridProps {
    children: ReactNode;
}

export const DashboardGrid = ({ children }: DashboardGridProps) => (
    <Grid container spacing="lg">{children}</Grid>
);

// Sortable item context
type SortableCtx = { attributes: DraggableAttributes; listeners: DraggableSyntheticListeners } | null;
const SortableItemContext = createContext<SortableCtx>(null);
export const useSortableItem = () => useContext(SortableItemContext);

interface SortableCardProps {
    id: CardId;
    children: ReactNode;
}

// SortableCard
export const SortableCard = ({ id, children }: SortableCardProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.9 : 1,
        width: "100%"
    };

    return (
        <SortableItemContext.Provider value={{ attributes, listeners }}>
            <div ref={setNodeRef} style={style}>
                {children}
            </div>
        </SortableItemContext.Provider>
    );
};

interface DraggableHandleProps {
    children: ReactElement;
    sx?: CSSProperties;
}

// DraggableHandle
export const DraggableHandle = ({ children, sx }: DraggableHandleProps) => {
    const ctx = useSortableItem();
    if (!ctx) return children;
    
    const { attributes, listeners } = ctx;
    return React.cloneElement(children, {
        ...attributes,
        ...listeners,
        style: { cursor: "grab", ...(children.props.style || {}), ...(sx || {}) }
    });
};